import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useUpdateResultCompletedAt } from '../../api/hooks/useResultCompletedAt';

interface Props {
  open: boolean;
  onClose: () => void;
  resultId: number | null;
  competitionId: number;
  initialValue: string | null;
  /** Pro zobrazeni — napr. "SDH Mniší · 100m překážek · 2. pokus" */
  resultDescription?: string;
}

export const EditCompletedAtDialog = ({
  open,
  onClose,
  resultId,
  competitionId,
  initialValue,
  resultDescription,
}: Props) => {
  const { enqueueSnackbar } = useSnackbar();
  const [value, setValue] = useState<Dayjs | null>(null);
  const mutation = useUpdateResultCompletedAt(competitionId);

  useEffect(() => {
    if (open) {
      setValue(initialValue ? dayjs(initialValue) : dayjs());
    }
  }, [open, initialValue]);

  const handleSave = () => {
    if (!resultId || !value) return;
    mutation.mutate(
      { id: resultId, completedAt: value.format('YYYY-MM-DDTHH:mm:ss') },
      {
        onSuccess: () => {
          enqueueSnackbar('Čas ukončení uložen', { variant: 'success' });
          onClose();
        },
        onError: (e: unknown) => {
          enqueueSnackbar('Chyba při ukládání: ' + String(e), { variant: 'error' });
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Upravit čas ukončení pokusu</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {resultDescription && (
            <Typography variant="body2" color="text.secondary">
              {resultDescription}
            </Typography>
          )}
          <Alert severity="info">
            Čas ukončení slouží jako referenční bod pro lhůtu pro podání protestu
            (Pravidlo 17 PS / Směrnice dorostu 2022 kap. 5). Změna je auditována.
          </Alert>
          <DateTimePicker
            label="Čas ukončení pokusu"
            value={value}
            onChange={setValue}
            format="DD.MM.YYYY HH:mm:ss"
            views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
            ampm={false}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Zrušit</Button>
        <Button onClick={handleSave} variant="contained" disabled={!value || mutation.isPending}>
          Uložit
        </Button>
      </DialogActions>
    </Dialog>
  );
};
